import * as React from 'react';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  type BoxProps,
  type DialogContentProps,
  type DialogActionsProps,
  type DialogProps,
  type DialogTitleProps,
  type DrawerProps,
  type PaperProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  appChrome,
  appChromeBorderColor,
  appChromeShadow,
  appChromeSurfaceFill,
  brandPlasmaAmbientBackground,
  palette,
  scrollbarSx,
  typographyTokens,
} from '../theme/tokens';
import { panelSx } from './design';

export const cleanOuterPaddingSx = {
  px: { xs: 0.75, sm: 1, md: 1.25 },
  py: { xs: 0.75, sm: 1 },
} as const;

export const cleanInsetSx = {
  px: 1.35,
} as const;

export const cleanDividerSx = {
  height: 1,
  my: 0.35,
  bgcolor: (theme: Theme) =>
    theme.palette.mode === 'dark'
      ? 'rgba(209,215,224,0.1)'
      : alpha(theme.palette.text.primary, 0.08),
} as const;

export function cleanRowHoverSx(theme: Theme) {
  return {
    transition: 'background-color .16s ease, color .16s ease, border-color .16s ease',
    '&:hover': {
      bgcolor: theme.palette.mode === 'dark'
        ? alpha(palette.dark.surfaceRaised, 0.38)
        : alpha(theme.palette.text.primary, 0.055),
    },
  } as const;
}

export function cleanOverlayPaperSx(theme: Theme) {
  const dark = theme.palette.mode === 'dark';

  return {
    overflow: 'hidden',
    isolation: 'isolate',
    borderRadius: 1,
    border: '1px solid',
    borderColor: appChromeBorderColor(theme),
    color: 'text.primary',
    bgcolor: appChromeSurfaceFill(theme, dark ? 0.84 : 0.88),
    backgroundImage: [
      dark
        ? 'linear-gradient(180deg, rgba(20,28,40,.44) 0%, rgba(12,18,27,.68) 100%)'
        : 'linear-gradient(180deg, rgba(255,255,255,.72) 0%, rgba(251,249,255,.9) 100%)',
      brandPlasmaAmbientBackground(theme.palette.mode),
    ].join(', '),
    backgroundBlendMode: 'normal, soft-light',
    backdropFilter: appChrome.backdropFilter,
    WebkitBackdropFilter: appChrome.backdropFilter,
    boxShadow: appChromeShadow(theme),
    '& .MuiDialogContent-root, & .MuiDrawer-paper': {
      ...scrollbarSx(theme.palette.mode),
    },
  } as const;
}

export function cleanMenuPaperSx(theme: Theme) {
  return {
    ...cleanOverlayPaperSx(theme),
    p: 0.4,
    minWidth: 188,
    '& .MuiList-root': {
      p: 0,
    },
  } as const;
}

type CleanPanelProps = PaperProps & {
  quiet?: boolean;
};

export function CleanPanel({ children, quiet = false, sx, ...props }: CleanPanelProps) {
  return (
    <Paper
      variant="outlined"
      {...props}
      sx={[
        (theme) => ({
          overflow: 'hidden',
          ...(quiet
            ? {
                border: '1px solid',
                borderColor: theme.palette.divider,
                bgcolor: theme.palette.background.paper,
                boxShadow: 'none',
              }
            : panelSx(theme)),
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Paper>
  );
}

export const CleanSurface = CleanPanel;

export function CleanCard(props: CleanPanelProps) {
  return <CleanPanel {...props} />;
}

type CleanCardHeaderProps = {
  title?: React.ReactNode;
  subheader?: React.ReactNode;
  action?: React.ReactNode;
  sx?: SxProps<Theme>;
  titleTypographyProps?: Record<string, unknown>;
  subheaderTypographyProps?: Record<string, unknown>;
};

export function CleanCardHeader({
  title,
  subheader,
  action,
  sx,
}: CleanCardHeaderProps) {
  return (
    <CleanPanelHeader
      title={title}
      subtitle={subheader}
      actions={action}
      sx={sx}
    />
  );
}

export function CleanCardContent({ sx, ...props }: BoxProps) {
  return (
    <Box
      {...props}
      sx={[
        {
          px: 1.35,
          py: 1.2,
          '&:last-child': {
            pb: 1.2,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}

export function CleanCardActions({ sx, ...props }: BoxProps) {
  return (
    <CleanActionBar
      align="end"
      divider="top"
      sx={sx}
    >
      {props.children}
    </CleanActionBar>
  );
}

type CleanPanelHeaderProps = {
  title?: React.ReactNode;
  titleEnd?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  density?: 'compact' | 'regular';
  divider?: boolean;
  sx?: SxProps<Theme>;
};

export function CleanPanelHeader({
  title,
  titleEnd,
  subtitle,
  actions,
  density = 'regular',
  divider = true,
  sx,
}: CleanPanelHeaderProps) {
  if (!title && !subtitle && !actions) return null;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      justifyContent="space-between"
      sx={[
        {
          ...cleanInsetSx,
          pt: density === 'compact' ? 0.55 : 1.05,
          pb: density === 'compact' ? 0.85 : 1.05,
          borderBottom: divider ? '1px solid' : 0,
          borderColor: 'divider',
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box sx={{ minWidth: 0 }}>
        {title && (
          <Stack direction="row" spacing={0.35} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                minWidth: 0,
                fontSize: 14.2,
                fontWeight: typographyTokens.weights.bold,
                lineHeight: 1.18,
                letterSpacing: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            {titleEnd}
          </Stack>
        )}
        {subtitle && (
          <Typography sx={{ mt: 0.45, fontSize: 12.4, lineHeight: 1.32, color: 'text.secondary', letterSpacing: 0 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>{actions}</Box>}
    </Stack>
  );
}

type CleanDialogTitleProps = Omit<DialogTitleProps, 'title'> & {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
};

export function CleanDialogTitle({ title, subtitle, actions, sx, ...props }: CleanDialogTitleProps) {
  return (
    <DialogTitle
      {...props}
      sx={[
        {
          p: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <CleanPanelHeader
        title={title}
        subtitle={subtitle}
        actions={actions}
        divider={false}
        sx={{ px: { xs: 2.25, sm: 3.25 }, py: { xs: 1.75, sm: 2.25 } }}
      />
    </DialogTitle>
  );
}

export function CleanDialogContent({ sx, ...props }: DialogContentProps) {
  return (
    <DialogContent
      {...props}
      sx={[
        {
          px: { xs: 2.25, sm: 3.25 },
          py: { xs: 2.25, sm: 3 },
          borderColor: 'divider',
          '& .MuiFormControl-root': {
            minWidth: 0,
          },
          '& .MuiTextField-root, & .MuiFormControl-root': {
            my: 0.2,
          },
          '& .MuiStack-root > .MuiTextField-root:first-of-type, & .MuiStack-root > .MuiFormControl-root:first-of-type': {
            mt: 0,
          },
          '& .MuiOutlinedInput-root': {
            minHeight: 48,
          },
          '& .MuiOutlinedInput-input': {
            py: 1.18,
            fontSize: 14.1,
            lineHeight: 1.36,
          },
          '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
            alignItems: 'flex-start',
            minHeight: 116,
            px: 1.35,
            py: 1.25,
          },
          '& .MuiOutlinedInput-root.MuiInputBase-multiline .MuiInputBase-input': {
            p: 0,
          },
          '&.MuiDialogContent-dividers': {
            borderTop: 0,
            borderBottom: 0,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}

export function CleanDialog({ PaperProps, ...props }: DialogProps) {
  return (
    <Dialog
      {...props}
      PaperProps={{
        ...PaperProps,
        sx: [
          (theme) => cleanOverlayPaperSx(theme),
          ...(Array.isArray(PaperProps?.sx) ? PaperProps.sx : PaperProps?.sx ? [PaperProps.sx] : []),
        ],
      }}
    />
  );
}

export function CleanDialogActions({ sx, ...props }: DialogActionsProps) {
  return (
    <DialogActions
      {...props}
      sx={[
        {
          px: { xs: 2.25, sm: 3.25 },
          py: { xs: 1.75, sm: 2.15 },
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          '& > :not(style) ~ :not(style)': {
            ml: 0,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}

type CleanActionBarProps = {
  children: React.ReactNode;
  align?: 'start' | 'end' | 'space-between';
  divider?: 'top' | 'bottom' | false;
  sx?: SxProps<Theme>;
};

export function CleanActionBar({ children, align = 'end', divider = false, sx }: CleanActionBarProps) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      justifyContent={align === 'space-between' ? 'space-between' : align === 'start' ? 'flex-start' : 'flex-end'}
      useFlexGap
      flexWrap="wrap"
      sx={[
        {
          px: 1.35,
          py: 1,
          borderTop: divider === 'top' ? '1px solid' : 0,
          borderBottom: divider === 'bottom' ? '1px solid' : 0,
          borderColor: 'divider',
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Stack>
  );
}

type CleanDrawerHeaderProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  sx?: SxProps<Theme>;
};

export function CleanDrawerHeader({ title, subtitle, actions, sx }: CleanDrawerHeaderProps) {
  return (
    <CleanPanelHeader
      title={title}
      subtitle={subtitle}
      actions={actions}
      sx={[
        {
          px: 1.5,
          pt: 1.25,
          pb: 1.15,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}

type CleanDrawerShellProps = DrawerProps & {
  width?: string | number | Record<string, string | number>;
};

export function CleanDrawerShell({ PaperProps, width = { xs: '100vw', sm: 480 }, anchor = 'right', ...props }: CleanDrawerShellProps) {
  return (
    <Drawer
      {...props}
      anchor={anchor}
      PaperProps={{
        ...PaperProps,
        sx: [
          (theme) => ({
            ...cleanOverlayPaperSx(theme),
            width,
            maxWidth: '100vw',
            overflowY: 'auto',
            borderRadius: 0,
            borderTop: 0,
            borderBottom: 0,
          }),
          ...(Array.isArray(PaperProps?.sx) ? PaperProps.sx : PaperProps?.sx ? [PaperProps.sx] : []),
        ],
      }}
    />
  );
}

type CleanMetricTileProps = {
  label: React.ReactNode;
  value: React.ReactNode;
  helper?: React.ReactNode;
  tone?: 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent';
  compact?: boolean;
  onClick?: () => void;
  sx?: SxProps<Theme>;
};

export function CleanMetricTile({
  label,
  value,
  helper,
  tone = 'neutral',
  compact = false,
  onClick,
  sx,
}: CleanMetricTileProps) {
  const content = (
    <Box
      sx={[
        (theme) => {
          const toneColor = {
            neutral: theme.palette.text.primary,
            info: theme.palette.info.main,
            success: theme.palette.success.main,
            warning: theme.palette.warning.main,
            danger: theme.palette.error.main,
            accent: theme.palette.primary.main,
          }[tone];
          const isToned = tone !== 'neutral';

          return {
            minHeight: compact ? 66 : 76,
            px: compact ? 1.15 : 1.35,
            py: compact ? 1 : 1.15,
            border: '1px solid',
            borderColor: isToned ? alpha(toneColor, theme.palette.mode === 'dark' ? 0.2 : 0.16) : theme.palette.divider,
            borderRadius: 1,
            bgcolor: theme.palette.mode === 'dark'
              ? alpha(palette.dark.surfaceElevated, 0.68)
              : theme.palette.background.paper,
            display: 'grid',
            gridTemplateRows: 'auto 1fr auto',
            gap: 0.4,
            minWidth: 0,
            transition: 'background-color .16s ease, border-color .16s ease',
            ...(isToned
              ? {
                  boxShadow: `inset 2px 0 0 ${alpha(toneColor, theme.palette.mode === 'dark' ? 0.72 : 0.52)}`,
                }
              : {}),
            '&:hover': onClick
              ? {
                  borderColor: isToned ? alpha(toneColor, theme.palette.mode === 'dark' ? 0.34 : 0.24) : alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.22 : 0.13),
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(palette.dark.surfaceRaised, 0.56)
                    : alpha(theme.palette.text.primary, 0.02),
                }
              : undefined,
          };
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: typographyTokens.weights.medium,
          color: 'text.secondary',
          lineHeight: 1.15,
          letterSpacing: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          alignSelf: 'center',
          fontSize: compact ? 23 : 26,
          fontWeight: typographyTokens.weights.metric,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      {helper && (
        <Typography color="text.secondary" sx={{ fontSize: 11.2, lineHeight: 1.2 }}>
          {helper}
        </Typography>
      )}
    </Box>
  );

  if (onClick) {
    return (
      <ButtonBase onClick={onClick} sx={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 1 }}>
        {content}
      </ButtonBase>
    );
  }

  return content;
}

type CleanIconActionProps = {
  label: string;
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  active?: boolean;
  sx?: SxProps<Theme>;
};

export function CleanIconAction({ label, children, onClick, disabled = false, active = false, sx }: CleanIconActionProps) {
  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="small"
          aria-label={label}
          onClick={onClick}
          disabled={disabled}
          sx={[
            (theme) => ({
              width: 32,
              height: 32,
              borderRadius: 0.75,
              color: active ? 'text.primary' : 'text.secondary',
              border: 0,
              bgcolor: 'transparent',
              transition: 'background-color .16s ease, color .16s ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: theme.palette.mode === 'dark'
                  ? alpha(palette.dark.surfaceRaised, 0.38)
                  : alpha(theme.palette.text.primary, 0.055),
              },
              '&.Mui-disabled': {
                opacity: 0.36,
              },
            }),
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          ]}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

type CleanPanelRowProps = {
  icon?: React.ReactNode;
  label: React.ReactNode;
  meta?: React.ReactNode;
  action?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  selected?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
};

export function CleanPanelRow({ icon, label, meta, action, onClick, selected = false, disabled = false, sx }: CleanPanelRowProps) {
  const content = (
    <>
      {icon && <Box sx={{ display: 'grid', placeItems: 'center', color: 'inherit', minWidth: 28 }}>{icon}</Box>}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            display: 'block',
            minWidth: 0,
            fontSize: 13,
            fontWeight: typographyTokens.weights.medium,
            lineHeight: 1.2,
            letterSpacing: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        {meta && (
          <Typography
            component="span"
            sx={{
              display: 'block',
              mt: 0.25,
              minWidth: 0,
              fontSize: 11.4,
              lineHeight: 1.25,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {meta}
          </Typography>
        )}
      </Box>
      {action && <Box sx={{ ml: 'auto', flexShrink: 0 }}>{action}</Box>}
    </>
  );

  const rowSx = [
    (theme: Theme) => ({
      width: '100%',
      minHeight: 40,
      px: 1.35,
      py: 0.75,
      borderRadius: 0.75,
      display: 'grid',
      gridTemplateColumns: `${icon ? '28px ' : ''}minmax(0, 1fr)${action ? ' auto' : ''}`,
      alignItems: 'center',
      gap: 0.5,
      color: disabled ? 'text.disabled' : selected ? 'text.primary' : 'text.primary',
      textAlign: 'left',
      bgcolor: selected
        ? theme.palette.mode === 'dark'
          ? alpha(palette.primary.main, 0.075)
          : alpha(theme.palette.primary.main, 0.08)
        : 'transparent',
      opacity: disabled ? 0.48 : 1,
      ...cleanRowHoverSx(theme),
    }),
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ];

  if (onClick) {
    return (
      <ButtonBase disabled={disabled} onClick={onClick} sx={rowSx}>
        {content}
      </ButtonBase>
    );
  }

  return <Box sx={rowSx}>{content}</Box>;
}

type CleanSegmentedOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
};

type CleanSegmentedFilterProps<T extends string> = {
  value: T | null;
  options: readonly CleanSegmentedOption<T>[];
  onChange: (value: T) => void;
  sx?: SxProps<Theme>;
};

export function CleanSegmentedFilter<T extends string>({ value, options, onChange, sx }: CleanSegmentedFilterProps<T>) {
  return (
    <Stack
      direction="row"
      spacing={0.35}
      alignItems="center"
      sx={[
        { minHeight: 40, py: 0.45 },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <ButtonBase
            key={option.value}
            disableRipple
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            sx={(theme) => ({
              height: 26,
              px: 0.95,
              borderRadius: 0.65,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: selected ? 'text.primary' : 'text.secondary',
              bgcolor: selected
                ? theme.palette.mode === 'dark'
                  ? alpha(palette.dark.surfaceRaised, 0.46)
                  : alpha(theme.palette.text.primary, 0.07)
                : 'transparent',
              fontSize: 12,
              fontWeight: selected ? typographyTokens.weights.semibold : typographyTokens.weights.medium,
              lineHeight: 1,
              transition: 'background-color .16s ease, color .16s ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: theme.palette.mode === 'dark'
                  ? alpha(palette.dark.surfaceRaised, 0.38)
                  : alpha(theme.palette.text.primary, 0.055),
              },
            })}
          >
            {option.label}
          </ButtonBase>
        );
      })}
    </Stack>
  );
}
